/**
 * Composable для управления интерактивными турами.
 * Обёртка над driver.js с поддержкой локализации и прогресса.
 */
import { ref, onUnmounted } from 'vue'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { useHelpProgress } from './useHelpProgress'
import { t } from '../../services/i18nService'

// Текущий активный драйвер (синглтон)
let activeDriver = null
// Відстеження таймаутів для очищення при unmount
let pendingTimeouts = new Set()

const scheduleTimeout = (fn, delay) => {
  const id = setTimeout(() => {
    pendingTimeouts.delete(id)
    fn()
  }, delay)
  pendingTimeouts.add(id)
  return id
}

const clearAllTimeouts = () => {
  pendingTimeouts.forEach(id => clearTimeout(id))
  pendingTimeouts.clear()
}

// Мінімальна ширина екрана для турів (тільки desktop)
const MIN_SCREEN_WIDTH = 769

/**
 * Перевіряє, чи дозволений запуск турів на поточному екрані.
 * Тури працюють тільки на великих екранах (>= 769px).
 */
const isToursEnabled = () => {
  return window.innerWidth >= MIN_SCREEN_WIDTH
}

export function useTour() {
  const { completeTour, isTourCompleted } = useHelpProgress()

  const isRunning = ref(false)
  const currentTourId = ref(null)

  /**
   * Создаёт и запускает тур на основе конфигурации.
   * @param {Object} tourConfig - конфигурация тура
   * @param {string} tourConfig.id - уникальный идентификатор тура
   * @param {Array} tourConfig.steps - массив шагов тура
   * @param {Object} tourConfig.options - дополнительные опции driver.js
   * @param {boolean} force - запустить даже если тур уже пройден
   */
  const startTour = (tourConfig, force = false) => {
    // Перевірка на мобільний пристрій - туры тільки на великих екранах
    if (!isToursEnabled()) {
      console.warn('[Tour] Tours are disabled on small screens')
      return false
    }

    // Если тур уже пройден и не принудительный запуск - пропускаем
    if (!force && isTourCompleted(tourConfig.id)) {
      return false
    }

    // Если уже запущен другой тур - останавливаем его
    if (activeDriver) {
      activeDriver.destroy()
      activeDriver = null
    }

    /**
     * Розгортає аккордеон за індексом і чекає на появу елемента.
     */
    const expandAccordion = (index) => {
      const accordions = document.querySelectorAll('.device-panel__body .sidebar-accordion')
      const accordion = accordions[index]
      if (accordion && !accordion.classList.contains('sidebar-accordion--expanded')) {
        const header = accordion.querySelector('.sidebar-accordion__header')
        if (header) header.click()
      }
    }

    /**
     * Підготовка наступного кроку - розгортання UI перед переходом.
     */
    const prepareStep = (stepIndex) => {
      const step = tourConfig.steps[stepIndex]
      if (!step) return

      // Розгортання аккордеона
      if (typeof step.expandAccordionIndex === 'number') {
        expandAccordion(step.expandAccordionIndex)
      }

      // Розгортання за селектором
      if (step.expandSelector) {
        const expandEl = document.querySelector(step.expandSelector)
        if (expandEl) {
          const accordion = expandEl.closest('.sidebar-accordion')
          if (accordion && !accordion.classList.contains('sidebar-accordion--expanded')) {
            expandEl.click()
          } else if (!accordion) {
            const isOpen = expandEl.getAttribute('aria-expanded') === 'true'
            if (!isOpen) expandEl.click()
          }
        }
      }
    }

    // Формируем шаги с локализацией
    const localizedSteps = tourConfig.steps.map((step) => ({
      element: step.element,
      popover: {
        title: step.titleKey ? t(step.titleKey) : step.title,
        description: step.descriptionKey ? t(step.descriptionKey) : step.description,
        side: step.side || 'bottom',
        align: step.align || 'center',
      },
    }))

    // Создаём драйвер с настройками
    activeDriver = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      nextBtnText: t('help.next'),
      prevBtnText: t('help.prev'),
      doneBtnText: t('help.finish'),
      progressText: t('help.progress'),
      popoverClass: 'cloudgps-tour-popover',
      overlayColor: 'rgba(0, 0, 0, 0.6)',
      stagePadding: 10,
      stageRadius: 8,
      allowClose: true,
      overlayClickNext: false,
      animate: true,
      smoothScroll: true,
      disableActiveInteraction: false,
      ...tourConfig.options,
      steps: localizedSteps,
      // Перед переходом на наступний крок - розгортаємо потрібні елементи
      onNextClick: () => {
        const nextIndex = activeDriver.getActiveIndex() + 1
        if (nextIndex < tourConfig.steps.length) {
          prepareStep(nextIndex)
          // Даємо час на анімацію розгортання перед переходом
          scheduleTimeout(() => activeDriver?.moveNext(), 150)
        } else {
          activeDriver.moveNext()
        }
      },
      // Перед переходом на попередній крок
      onPrevClick: () => {
        const prevIndex = activeDriver.getActiveIndex() - 1
        if (prevIndex >= 0) {
          prepareStep(prevIndex)
          scheduleTimeout(() => activeDriver?.movePrevious(), 150)
        } else {
          activeDriver.movePrevious()
        }
      },
      onDestroyStarted: () => {
        // Очищаємо таймаути при закритті туру
        clearAllTimeouts()
        // При закрытии тура отмечаем его как завершённый
        if (activeDriver) {
          // Проверяем, дошёл ли пользователь до конца
          const currentStep = activeDriver.getActiveIndex()
          const totalSteps = localizedSteps.length
          if (currentStep === totalSteps - 1) {
            completeTour(tourConfig.id)
          }
          activeDriver.destroy()
          activeDriver = null
          isRunning.value = false
          currentTourId.value = null
        }
      },
      onDestroyed: () => {
        isRunning.value = false
        currentTourId.value = null
      },
    })

    // Підготовка першого кроку перед запуском
    prepareStep(0)

    // Запускаем тур з невеликою затримкою для анімації
    isRunning.value = true
    currentTourId.value = tourConfig.id
    scheduleTimeout(() => activeDriver?.drive(), 150)
    return true
  }

  /**
   * Останавливает текущий тур.
   */
  const stopTour = () => {
    clearAllTimeouts()
    if (activeDriver) {
      activeDriver.destroy()
      activeDriver = null
    }
    isRunning.value = false
    currentTourId.value = null
  }

  /**
   * Подсвечивает конкретный элемент без запуска полного тура.
   * @param {string} selector - CSS селектор элемента
   * @param {Object} popover - содержимое поповера
   */
  const highlightElement = (selector, popover) => {
    if (activeDriver) {
      activeDriver.destroy()
    }

    activeDriver = driver({
      popoverClass: 'cloudgps-tour-popover',
      overlayColor: 'rgba(0, 0, 0, 0.5)',
      stagePadding: 8,
      stageRadius: 8,
    })

    activeDriver.highlight({
      element: selector,
      popover: {
        title: popover.titleKey ? t(popover.titleKey) : popover.title,
        description: popover.descriptionKey ? t(popover.descriptionKey) : popover.description,
        side: popover.side || 'bottom',
        align: popover.align || 'center',
      },
    })
  }

  // Очищаем при размонтировании компонента
  onUnmounted(() => {
    clearAllTimeouts()
    if (activeDriver && currentTourId.value) {
      activeDriver.destroy()
      activeDriver = null
    }
  })

  return {
    isRunning,
    currentTourId,
    startTour,
    stopTour,
    highlightElement,
  }
}
